import { createContext, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Bounce, toast } from 'react-toastify';
import { urlWebSockt } from '../services/api';
import { io } from 'socket.io-client';

export const HomeContext = createContext({});

export const HomeProvider = () => {
  const navigate = useNavigate();

  const [user, setUser] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const [userOn, setUserOn] = useState({});
  const [modalAvisoOpen, setModalAvisoOpen] = useState(false);

  const reconnectAttempts = useRef(0);
  const userRef = useRef(null);
  const connectionWarningShown = useRef(false);
  const MAX_RECONNECT_ATTEMPTS = 2;

  const [socket, setSocket] = useState(null);
  const token = sessionStorage.getItem('connectionsToken');

  const headerApi = useMemo(
    () => ({
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
    [token],
  );

  useEffect(() => {
    if (!token) {
      sessionStorage.clear();
      navigate('/login');
    }
  }, [navigate, token]);

  useEffect(() => {
    if (!token) return undefined;

    const newSocket = io(urlWebSockt, {
      path: '/67ZlfPVt/socket.io',
      auth: {
        token: token,
      },
      transports: ['websocket', 'polling'],
      reconnectionDelay: 2000,
    });

    setSocket(newSocket);

    newSocket.on('connect', () => {
      reconnectAttempts.current = 0;
      connectionWarningShown.current = false;
    });

    newSocket.on('user_data', (userData) => {
      userRef.current = userData;
      setUser(userData);
    });

    newSocket.on('user_updated', (userData) => {
      userRef.current = userData;
      setUser(userData);
    });

    newSocket.on('changed_user', (userId) => {
      setUserOn(userId);
    });

    newSocket.on('User_desconectado_sessao', (userId) => {
      if (userRef.current?.id === userId) {
        sessionStorage.clear();
        navigate('/login');

        toast.warn('Usuário desconectado!', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'dark',
          transition: Bounce,
        });
      }
    });

    newSocket.on('connect_error', (err) => {
      const errorMessage = err?.data?.message || err?.message || '';
      const invalidToken = errorMessage.toLowerCase().includes('invalid token');

      if (invalidToken) {
        sessionStorage.clear();
        navigate('/login');
        toast.warn('Sessão expirada', {
          position: 'top-right',
          autoClose: 5000,
          theme: 'dark',
          transition: Bounce,
        });
        return;
      }

      reconnectAttempts.current += 1;
      if (
        reconnectAttempts.current >= MAX_RECONNECT_ATTEMPTS &&
        !connectionWarningShown.current
      ) {
        connectionWarningShown.current = true;
        toast.warn('Falha ao conectar com o servidor. Tentando novamente…', {
          position: 'top-right',
          autoClose: 5000,
          theme: 'dark',
          transition: Bounce,
        });
      }
    });

    return () => {
      newSocket.disconnect();
    };
  }, [navigate, token]);

  return (
    <HomeContext.Provider
      value={{
        user,
        navigate,
        isOpen,
        setIsOpen,
        socket,
        userOn,
        headerApi,
        modalAvisoOpen,
        setModalAvisoOpen,
      }}
    >
      <Outlet />
    </HomeContext.Provider>
  );
};
